import AddIcon from "@mui/icons-material/Add";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import HomeIcon from "@mui/icons-material/Home";
import { Badge, IconButton } from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setClientID, setProjectID } from "../../../../Redux/Actions/actions";
import { DashboardContext } from "../../DashboardContext/DashboardContextProvider";
import CreateFolderPopover from "./CreateFolderPopover/CreateFolderPopover";
import "./styles/project-file-structure.css";

// Component to display a specific project's folder
const ProjectFolder = () => {
  const {
    client,
    clientId,
    projectId,
    isAdmin,
    navigate,
    setHeading,
    queryClient,
  } = useContext(DashboardContext);

  const [userType, setUserType] = useState(isAdmin ? "admin" : "client");
  const [openAddFolder, setOpenAddFolder] = useState(false);

  const dispatch = useDispatch();
  // Query to get all files
  const {
    data: folders,
    isFetched,
    isLoading,
  } = useQuery({
    queryKey: ["all-project-structure", projectId, userType],
    queryFn: async () => {
      const res = await axios.get(
        `/api/all-project-structure/${projectId}/${userType}/`,
        {
          withCredentials: true,
        },
      );
      return res.data;
    },
    enabled: !!projectId,
  });

  useEffect(() => {
    setHeading(folders && isFetched ? folders.projectName : "Project Files");
    console.log("FOLDERS: ", folders);
  }, [folders, isFetched]);

  return (
    <div className="project-files-container">
      <div className="folders-container">
        <div className="breadcrumbs-container">
          <Breadcrumbs
            separator="/"
            aria-label="breadcrumb"
            classes={{
              root: "breadcrumbs-root",
              separator: "breadcrumbs-separator",
            }}
          >
            {isAdmin && (
              <Button
                className="breadcrumbs-btn root-link-btn"
                variant="text"
                onClick={() => {
                  dispatch(setClientID(null));
                  dispatch(setProjectID(null));
                  navigate("/dashboard/files");
                }}
                startIcon={<HomeIcon id="root-folder-icon" />}
              >
                <Typography
                  variant="body1"
                  className="breadcrumbs-value root-text"
                >
                  Clients Folder
                </Typography>
              </Button>
            )}
            {!isAdmin && (
              <Button
                className="breadcrumbs-btn root-link-btn"
                variant="text"
                onMouseOver={async (e) => {
                  e.preventDefault();

                  await queryClient.prefetchQuery({
                    queryKey: ["client-structure", clientId],
                    queryFn: async () => {
                      const res = await axios.get(
                        `/api/client-structure/${clientId}/`,
                        {
                          withCredentials: true,
                        },
                      );
                      return res.data;
                    },
                    staleTime: 120000,
                  });
                }}
                onClick={() => {
                  dispatch(setProjectID(null));
                  navigate(`/dashboard/files/${clientId}`);
                }}
                startIcon={<HomeIcon id="root-folder-icon" />}
              >
                <Typography
                  variant="body1"
                  className="breadcrumbs-value root-text"
                >
                  {isFetched && folders.clientName
                    ? `${folders.clientName}`
                    : "Home Folder"}
                </Typography>
              </Button>
            )}
            {isAdmin && (
              <Button
                className="breadcrumbs-btn root-link-btn"
                variant="text"
                onMouseOver={async (e) => {
                  e.preventDefault();

                  await queryClient.prefetchQuery({
                    queryKey: ["client-structure", clientId],
                    queryFn: async () => {
                      const res = await axios.get(
                        `/api/client-structure/${clientId}/`,
                        {
                          withCredentials: true,
                        },
                      );
                      return res.data;
                    },
                    staleTime: 120000,
                  });
                }}
                onClick={() => {
                  dispatch(setProjectID(null));
                  navigate(`/dashboard/files/${clientId}`);
                }}
              >
                <Typography
                  variant="body1"
                  className="breadcrumbs-value root-text"
                >
                  {isFetched
                    ? folders.clientName
                      ? `${folders.clientName} Folder`
                      : "Client Folder"
                    : "Client Folder"}
                </Typography>
              </Button>
            )}
            <Typography
              variant="body1"
              className="breadcrumbs-value current-text"
            >
              {isFetched && folders.projectName
                ? `${folders.projectName} Folder`
                : "Project Folder"}
            </Typography>
          </Breadcrumbs>
        </div>
        {isFetched && folders.folders.length > 0
          ? folders.folders.map((folder) => (
              <Badge
                className="folder-badge"
                id="folder-badge"
                badgeContent={folder?.files.length}
                key={folder.id}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <div
                  // key={folder.id}
                  className={`folder-container`}
                  onMouseOver={async (e) => {
                    e.preventDefault();
                    await queryClient.prefetchQuery({
                      queryKey: [
                        "all-project-files",
                        projectId,
                        folder.name,
                        userType,
                      ],
                      queryFn: async () => {
                        const response = await axios.get(
                          `/api/uploaded-project-files/${projectId}/${folder.name}/${userType}`,
                        );
                        return response.data;
                      },
                    });
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(
                      `/dashboard/files/${clientId}/${projectId}/${folder.name}`,
                    );
                  }}
                >
                  <SvgIcon
                    className="folder"
                    component={FolderRoundedIcon}
                    viewBox="3.6 3.6 16.8 16.8"
                  />
                  <p className="folder-name">{folder.displayName}</p>
                </div>
              </Badge>
            ))
          : null}
        <IconButton
          id="upload-folder-icon-btn"
          onClick={() => setOpenAddFolder(true)}
        >
          <AddIcon className="new-folder-icon" id="add-folder-icon" />
        </IconButton>
      </div>
      <CreateFolderPopover
        open={openAddFolder}
        setOpen={setOpenAddFolder}
        projectId={projectId}
        queryClient={queryClient}
        userType={userType}
      />
    </div>
  );
};

export default ProjectFolder;
